import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  Phone,
  Rat,
  Zap,
  CircleDot,
  Footprints,
  Bug,
  Crosshair,
  TreePine,
  BedDouble,
  Timer,
  ShieldCheck,
  FileCheck,
  MapPinned,
  Star,
} from "lucide-react";
import { HeroLines, Reveal, ChapterHeading } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import {
  PESTS,
  GUARANTEES,
  REVIEWS,
  PHONE_DISPLAY,
  PHONE_LINK,
  HOURS,
  ZONE,
} from "../data/content";

const ICONS = { Rat, Zap, CircleDot, Footprints, Bug, Crosshair, TreePine, BedDouble, Timer, ShieldCheck, FileCheck, MapPinned };

const Hero = () => (
  <section data-testid="hero-section" className="hexa-bg relative overflow-hidden border-b border-black">
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-16 md:pb-24 grid gap-12 lg:grid-cols-12 items-end">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]"
            data-testid="hero-overline"
          >
            Lutte anti-nuisibles — Cahors, Lot (46)
          </motion.p>

          <HeroLines
            className="font-display uppercase leading-[0.9] tracking-tight text-[15vw] sm:text-[11vw] lg:text-[6.2rem] xl:text-[7rem] mt-6 whitespace-nowrap"
            lines={[
              { text: "Cibler." },
              { text: "Éliminer." },
              { text: "Protéger.", className: "text-[#E50914]" },
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-base md:text-lg text-neutral-600"
          >
            Quercy Nuisibles traite rats, guêpes, frelons, puces, fourmis,
            blattes, moustiques, chenilles processionnaires... — dans tout le
            Lot et les départements limitrophes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href={PHONE_LINK}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-3 bg-black text-white px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-300 hover:bg-[#E50914]"
            >
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
            <Link
              to="/contact"
              data-testid="hero-devis-button"
              className="inline-flex items-center gap-3 border border-black bg-white text-black px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-300 hover:bg-black hover:text-white"
            >
              Devis gratuit <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden"
          >
            <img
              src="/logo-transparent.png"
              alt="Logo Quercy Nuisibles"
              className="h-[340px] md:h-[520px] w-full object-contain"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-6 grid grid-cols-2 gap-px bg-black border border-black text-white"
          >
            <div className="bg-black p-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Horaires</p>
              <p className="text-sm font-semibold mt-1">{HOURS}</p>
            </div>
            <div className="bg-black p-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Zone</p>
              <p className="text-sm font-semibold mt-1">{ZONE}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
);

const ChapterIdentite = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} data-testid="chapter-identite" className="py-24 md:py-32">
      <div className="max-w-screen-xl mx-auto px-4 md:px-8 grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7">
          <ChapterHeading num="01" title="Basés à Cahors. Actifs partout dans le Lot." />
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg text-neutral-600 max-w-xl">
              Quercy Nuisibles est une entreprise locale de lutte contre les
              nuisibles. Notre approche est directe : un diagnostic précis, une
              méthode éprouvée, un résultat durable.
            </p>
            <p className="mt-4 text-base md:text-lg text-neutral-600 max-w-xl">
              Du centre-ville de Cahors aux villages les plus reculés du
              département — et au-delà, dans les départements limitrophes —
              nous intervenons rapidement, avec du matériel professionnel et
              des produits homologués.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/interventions"
              data-testid="identite-interventions-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-black border-b-2 border-[#E50914] pb-1 transition-colors duration-300 hover:text-[#E50914]"
            >
              Découvrir nos interventions <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="relative border border-black hard-shadow-red overflow-hidden">
              <motion.img
                src="/vehicule.jpg"
                alt="Véhicule d'intervention Quercy Nuisibles"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[300px] md:h-[420px] w-full object-cover"
                data-testid="vehicule-image"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ChapterCibles = () => (
  <section data-testid="chapter-cibles" className="py-24 md:py-32 border-t border-black">
    <div className="max-w-screen-xl mx-auto px-4 md:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <ChapterHeading num="02" title="Nos cibles" />
        <Reveal delay={0.1}>
          <Link
            to="/interventions"
            data-testid="cibles-see-all-link"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] border border-black px-5 py-3 transition-colors duration-300 hover:bg-black hover:text-white"
          >
            Tout voir <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-px bg-black border border-black md:grid-cols-2 lg:grid-cols-4">
        {PESTS.map((pest, i) => {
          const Icon = ICONS[pest.icon];
          const wide = i === 0 || i === 1;
          return (
            <Reveal key={pest.id} delay={i * 0.05} className={wide ? "md:col-span-2" : ""}>
              <Link
                to="/interventions"
                data-testid={`pest-card-${pest.id}`}
                className="group relative flex h-full min-h-[220px] flex-col justify-between bg-white p-6 md:p-8 transition-colors duration-300 hover:bg-black"
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs font-semibold tracking-[0.2em] text-neutral-400 transition-colors duration-300 group-hover:text-neutral-500">
                    [{pest.num}]
                  </span>
                  <Icon className="h-7 w-7 text-black transition-colors duration-300 group-hover:text-[#E50914]" />
                </div>
                <div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight text-black transition-colors duration-300 group-hover:text-white">
                    {pest.name}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-500 transition-colors duration-300 group-hover:text-neutral-400 line-clamp-2">
                    {pest.short}
                  </p>
                </div>
              </Link>
            </Reveal>
          );
        })}
        <Reveal delay={0.35} className="md:col-span-2 lg:col-span-2">
          <Link
            to="/contact"
            data-testid="pest-card-devis"
            className="group flex h-full min-h-[220px] flex-col justify-between bg-[#E50914] p-6 md:p-8 transition-colors duration-300 hover:bg-black"
          >
            <ArrowUpRight className="h-8 w-8 text-white" />
            <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight text-white">
              Autre nuisible ? Parlons-en.
            </h3>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ChapterGaranties = () => (
  <section data-testid="chapter-garanties" className="py-24 md:py-32 border-t border-black">
    <div className="max-w-screen-xl mx-auto px-4 md:px-8">
      <ChapterHeading num="03" title="Nos garanties" />
      <div className="mt-14 grid gap-px bg-black border border-black sm:grid-cols-2 lg:grid-cols-4">
        {GUARANTEES.map((g, i) => {
          const Icon = ICONS[g.icon];
          return (
            <Reveal key={g.title} delay={i * 0.08}>
              <div
                data-testid={`guarantee-${i}`}
                className="group h-full bg-white p-6 md:p-8 transition-colors duration-300 hover:bg-black"
              >
                <Icon className="h-8 w-8 text-[#E50914]" />
                <h3 className="mt-6 font-display uppercase text-xl tracking-tight text-black transition-colors duration-300 group-hover:text-white">
                  {g.title}
                </h3>
                <p className="mt-3 text-sm text-neutral-500 transition-colors duration-300 group-hover:text-neutral-400">
                  {g.text}
                </p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const ChapterAvis = () => (
  <section data-testid="chapter-avis" className="py-24 md:py-32 border-t border-black">
    <div className="max-w-screen-xl mx-auto px-4 md:px-8">
      <ChapterHeading num="04" title="Ils nous ont fait confiance" />
      <div className="mt-14 grid gap-8 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1}>
            <figure
              data-testid={`review-${i}`}
              className="h-full border border-black p-8 transition-shadow duration-300 hover:hard-shadow"
            >
              <div className="flex gap-1 text-[#E50914]">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-6 text-base text-neutral-700">
                « {r.text} »
              </blockquote>
              <figcaption className="mt-6 text-xs font-semibold uppercase tracking-[0.2em]">
                {r.name} — <span className="text-[#E50914]">{r.place}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="final-cta" className="bg-black text-white border-t border-black">
    <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-24 md:py-32">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">
          [05] — Urgence
        </p>
        <h2 className="font-display uppercase leading-[0.9] tracking-tight text-[13vw] md:text-8xl mt-6">
          Une invasion ?
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-400">
          Chaque jour compte face à une colonie qui s'installe. Appelez
          maintenant, on s'occupe du reste.
        </p>
        <a
          href={PHONE_LINK}
          data-testid="final-cta-phone"
          className="mt-10 inline-flex items-center gap-4 font-display uppercase tracking-tight text-4xl md:text-6xl text-white transition-colors duration-300 hover:text-[#E50914]"
        >
          <Phone className="h-8 w-8 md:h-12 md:w-12 text-[#E50914]" />
          {PHONE_DISPLAY}
        </a>
        <p className="mt-6 text-sm text-neutral-500">{HOURS} · {ZONE}</p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <ChapterIdentite />
      <ChapterCibles />
      <ChapterGaranties />
      <ChapterAvis />
      <FinalCTA />
    </main>
  );
}
